// (c) chaehongee. Confidential and proprietary.

import { useMemo } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import type { Dispatch } from '@reduxjs/toolkit'
import { addNode, toggleNode, setNodes } from '../../../store/slices/selectionSlice'
import type { Corner, Edge } from '../features/framing/types'
import type { ToolMode } from '../features/toolbar/useToolMode'

interface WorldItemsHandlers {
    onImageSelect: (id: string) => void
    onImageMoveStart: (id: string, e: React.PointerEvent, src?: string) => void
    onImageResizeStart: (id: string, src: string, corner: Corner, e: React.PointerEvent) => void
}

interface UseCanvasHandlersParams {
    // World items
    worldItemsHandlers: WorldItemsHandlers
    worldItemsState: {
        selectedWorldItemId: string | null
        clearLocalSelection: () => void
    }

    // Framing
    frameModeById: Record<string, boolean>

    // New Layer Handlers
    onLayerMove?: (frameId: string, layerId: string, e: React.PointerEvent) => void
    onLayerResize?: (frameId: string, layerId: string, corner: Corner, e: React.PointerEvent) => void

    // Backward compatibility / Fallback for 1:1 (Deprecated)
    onImageMoveInFrame?: (id: string, e: React.PointerEvent) => void
    onImageResizeInFrame?: (id: string, corner: Corner, e: React.PointerEvent) => void

    onFrameSelect: (id: string) => void
    onFrameMoveStart: (id: string, e: React.PointerEvent) => void
    onFrameResizeStart: (id: string, edge: Edge, e: React.PointerEvent) => void

    // Layer management
    onReorderLayers?: (frameId: string, fromIndex: number, toIndex: number) => void
    onRemoveLayer?: (frameId: string, layerId: string) => void

    // Image loading
    handleImageLoad: (src?: string) => (e: React.SyntheticEvent<HTMLImageElement>) => void
    handleVideoLoad?: (src?: string) => (e: React.SyntheticEvent<HTMLVideoElement>) => void

    // Selection
    selectedNodeIds: string[]
    sessionId: string | null
    dispatch: Dispatch

    // Tool mode
    toolMode: ToolMode

    // Context menu
    openContextMenu: (target: { id: string; src: string }, e: MouseEvent) => void

    // History
    withHistoryCapture: <T extends (...args: any[]) => any>(fn: T) => T
    withInstantHistoryCapture: <T extends (...args: any[]) => any>(fn: T) => T
}

export interface CanvasHandlers {
    // Image handlers
    onImageSelect: (id: string, e?: React.PointerEvent) => void
    onImageMoveStart: (id: string, e: React.PointerEvent, src?: string) => void
    onImageResizeStart: (id: string, src: string, corner: Corner, e: React.PointerEvent) => void

    // Layer Handlers
    onLayerMoveStart?: (frameId: string, layerId: string, e: React.PointerEvent) => void
    onLayerResizeStart?: (frameId: string, layerId: string, corner: Corner, e: React.PointerEvent) => void

    onImageLoad: (src?: string) => (e: React.SyntheticEvent<HTMLImageElement>) => void
    onVideoLoad?: (src?: string) => (e: React.SyntheticEvent<HTMLVideoElement>) => void
    onImageError: (src?: string) => void
    onImageContextMenu: (id: string, e: ReactMouseEvent, src: string) => void

    // Frame handlers
    onFrameSelect: (id: string) => void
    onFrameMoveStart: (id: string, e: React.PointerEvent) => void
    onFrameResizeStart: (id: string, edge: Edge, e: React.PointerEvent) => void

    // Layer management
    onReorderLayers?: (frameId: string, fromIndex: number, toIndex: number) => void
    onRemoveLayer?: (frameId: string, layerId: string) => void
}

/**
 * Unified Canvas Handlers Hook
 *
 * Creates all Canvas event handlers in a single, flat composition.
 * Handles routing between world items and frame modes with history capture.
 */
export function useCanvasHandlers({
    worldItemsHandlers,
    worldItemsState,
    frameModeById,
    onLayerMove,
    onLayerResize,
    onImageMoveInFrame,
    onImageResizeInFrame,
    onFrameSelect,
    onFrameMoveStart,
    onFrameResizeStart,
    onReorderLayers,
    onRemoveLayer,
    handleImageLoad,
    handleVideoLoad,
    selectedNodeIds,
    sessionId,
    dispatch,
    toolMode,
    openContextMenu,
    withHistoryCapture,
    withInstantHistoryCapture,
}: UseCanvasHandlersParams): CanvasHandlers {
    return useMemo(
        () => ({
            // Image selection with Redux sync and tool mode awareness
            onImageSelect: (id: string, e?: React.PointerEvent) => {
                if (toolMode === 'grab') return // grab mode: no item selection

                // Shift+click toggles item in/out of selection
                if (e?.shiftKey && sessionId) {
                    dispatch(toggleNode({ sessionId, nodeId: id }))
                    worldItemsHandlers.onImageSelect(id)
                    return
                }

                if (toolMode === 'select') {
                    // Select mode: additive
                    if (sessionId) dispatch(addNode({ sessionId, nodeId: id }))
                } else {
                    // Pointer mode: replace selection
                    if (sessionId) dispatch(setNodes({ sessionId, nodeIds: [id] }))
                }

                worldItemsHandlers.onImageSelect(id)
            },

            // Image move with frame mode routing and history
            onImageMoveStart: withHistoryCapture(
                (id: string, e: React.PointerEvent, src?: string) => {
                    if (frameModeById[id]) {
                        if (onLayerMove) {
                            // Assume 1:1 for legacy calls or primary layer
                            onLayerMove(id, id, e)
                        } else if (onImageMoveInFrame) {
                            onImageMoveInFrame(id, e)
                        }
                    } else {
                        worldItemsHandlers.onImageMoveStart(id, e, src)
                    }
                }
            ),

            // Layer move explicit handler
            onLayerMoveStart: onLayerMove ? withHistoryCapture(
                (frameId: string, layerId: string, e: React.PointerEvent) => {
                     if (frameModeById[frameId]) {
                        onLayerMove(frameId, layerId, e)
                     } else {
                        worldItemsHandlers.onImageMoveStart(frameId, e, '')
                     }
                }
            ) : undefined,

            // Image resize with frame mode routing and history
            onImageResizeStart: withHistoryCapture(
                (id: string, src: string, corner: Corner, e: React.PointerEvent) => {
                    if (frameModeById[id]) {
                        if (onLayerResize) {
                            onLayerResize(id, id, corner, e)
                        } else if (onImageResizeInFrame) {
                            onImageResizeInFrame(id, corner, e)
                        }
                    } else {
                        worldItemsHandlers.onImageResizeStart(id, src, corner, e)
                    }
                }
            ),

            // Layer resize explicit handler
            onLayerResizeStart: onLayerResize ? withHistoryCapture(
                (frameId: string, layerId: string, corner: Corner, e: React.PointerEvent) => {
                    if (frameModeById[frameId]) {
                        onLayerResize(frameId, layerId, corner, e)
                    } else {
                        worldItemsHandlers.onImageResizeStart(frameId, '', corner, e)
                    }
                }
            ) : undefined,

            // Image loading handlers
            onImageLoad: handleImageLoad,
            onVideoLoad: handleVideoLoad,
            onImageError: (src?: string) => console.warn('Canvas image failed', src),

            // Context menu
            onImageContextMenu: (id: string, e: ReactMouseEvent, src: string) => {
                openContextMenu({ id, src }, e as unknown as MouseEvent)
            },

            // Frame handlers with history
            onFrameSelect,
            onFrameMoveStart: withHistoryCapture(onFrameMoveStart),
            onFrameResizeStart: withHistoryCapture(onFrameResizeStart),

            // Layer management handlers with history (instant: begin + commit in one call)
            onReorderLayers: onReorderLayers ? withInstantHistoryCapture(onReorderLayers) : undefined,
            onRemoveLayer: onRemoveLayer ? withInstantHistoryCapture(onRemoveLayer) : undefined,
        }),
        [
            worldItemsHandlers,
            worldItemsState.selectedWorldItemId,
            worldItemsState.clearLocalSelection,
            frameModeById,
            onLayerMove,
            onLayerResize,
            onImageMoveInFrame,
            onImageResizeInFrame,
            onFrameSelect,
            onFrameMoveStart,
            onFrameResizeStart,
            onReorderLayers,
            onRemoveLayer,
            handleImageLoad,
            handleVideoLoad,
            selectedNodeIds,
            sessionId,
            dispatch,
            toolMode,
            openContextMenu,
            withHistoryCapture,
            withInstantHistoryCapture,
        ]
    )
}
